import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, Phone, Mail, FileText, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiCall } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location.state?.user;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    cuit: "",
    phone: "",
    email: userData?.email || "",
  });

  if (!userData) {
    navigate("/", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Ingresa el nombre de tu empresa");
    if (!form.cuit.trim()) return toast.error("Ingresa el CUIT");
    if (!form.phone.trim()) return toast.error("Ingresa un telefono de contacto");
    setLoading(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/auth/onboarding`, form);
      if (ok) {
        toast.success("Empresa creada. Bienvenido a Veritel!");
        navigate("/dashboard", { replace: true, state: { user: { ...userData, ...data, role: "company", needs_onboarding: false } } });
      } else {
        toast.error(data?.detail || "Error al registrar");
      }
    } catch { toast.error("Error de conexion"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="onboarding-page">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-violet-600 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Bienvenido a Veritel</h1>
          <p className="text-zinc-400 text-sm mt-2">Completa los datos de tu empresa para comenzar</p>
          {userData?.name && <p className="text-xs text-zinc-500 mt-1">Hola, {userData.name}</p>}
        </div>
        <form onSubmit={handleSubmit} className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-1.5 flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-400" />Nombre de la Empresa</Label>
            <Input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} placeholder="Mi Empresa S.A." className="bg-zinc-950 border-zinc-800 text-white" data-testid="onboarding-company" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-1.5 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" />CUIT</Label>
            <Input value={form.cuit} onChange={e => setForm(f => ({...f, cuit: e.target.value}))} placeholder="30-12345678-9" className="bg-zinc-950 border-zinc-800 text-white" data-testid="onboarding-cuit" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-1.5 flex items-center gap-2"><Phone className="w-4 h-4 text-green-400" />Telefono de Contacto</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+549381..." className="bg-zinc-950 border-zinc-800 text-white" data-testid="onboarding-phone" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-1.5 flex items-center gap-2"><Mail className="w-4 h-4 text-amber-400" />Email de Contacto</Label>
            <Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="contacto@empresa.com" type="email" className="bg-zinc-950 border-zinc-800 text-white" data-testid="onboarding-email" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 text-white h-11 mt-2" data-testid="onboarding-submit">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registrando...</> : <><ArrowRight className="w-4 h-4 mr-2" />Crear Empresa y Comenzar</>}
          </Button>
          <p className="text-[11px] text-zinc-600 text-center">Comenzas con un plan Free de $500 de saldo.</p>
        </form>
      </div>
    </div>
  );
}
